/**
 * Onchain Spread Watcher DTOs
 *
 * Shared types for the onchain spread watcher feature.
 * Used by both the Kenobi backend and the orderbook-spread-analysis frontend.
 *
 * Convention:
 * - Types here are pure data transfer objects (DTOs) describing API shapes.
 * - Zod validation schemas remain in the Kenobi application layer.
 * - The `protocol` field uses the `DexProtocol` type from the onchainSwap module.
 */
import type { DexProtocol } from "../onchainSwap";

// ============================================================================
// Health Status
// ============================================================================

export type OnchainWatcherHealthStatus = "healthy" | "degraded" | "error" | "no_data";

export interface OnchainWatcherHealthError {
  message: string;
  timestamp: number;
  count: number;
}

// ============================================================================
// Active Watchers
// ============================================================================

export interface OnchainActiveWatcherDTO {
  chainId: string;
  tokenIn: string;
  tokenOut: string;
  protocol: DexProtocol;
  tokenInSymbol: string;
  tokenOutSymbol: string;
  pairDisplay: string;
  owner?: string;
  startedAt: string;
  expireAt: string;
}

export type GetOnchainActiveWatchersResponse = OnchainActiveWatcherDTO[];

// ============================================================================
// Watcher Health
// ============================================================================

export interface OnchainWatcherHealthDTO {
  chainId: string;
  tokenIn: string;
  tokenOut: string;
  protocol: DexProtocol;
  lastSnapshotAt: number | null;
  snapshotCount: number;
  lastError: OnchainWatcherHealthError | null;
  status: OnchainWatcherHealthStatus;
  updatedAt: number;
}

export type GetOnchainWatcherHealthResponse = OnchainWatcherHealthDTO[];

// ============================================================================
// Snapshots
// ============================================================================

export interface OnchainSnapshotDTO {
  timestamp: number;
  buyImpactPct: string;
  sellImpactPct: string;
  poolFeePct: string;
  totalCostPct: string;
}

export type GetOnchainSnapshotsResponse = OnchainSnapshotDTO[];

// ============================================================================
// Popular Tokens
// ============================================================================

export interface OnchainSpreadPopularTokenDTO {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logo?: string;
  price?: number;
  liquidity?: number;
}

export type GetOnchainPopularTokensResponse = OnchainSpreadPopularTokenDTO[];

// ============================================================================
// Token Info
// ============================================================================

export interface OnchainSpreadTokenInfoDTO {
  address?: string;
  symbol: string;
  name: string;
  decimals: number;
  logo?: string;
  price?: number;
}

// ============================================================================
// Route Check
// ============================================================================

export interface OnchainRouteCheckDTO {
  hasRoute: boolean;
}

// ============================================================================
// Config
// ============================================================================

export interface OnchainSpreadConfigDTO {
  quoteAmountUsd: number;
}

// ============================================================================
// Requests
// ============================================================================

export interface StartOnchainWatchingRequest {
  chainId: string;
  tokenIn: string;
  tokenOut: string;
  protocol: DexProtocol;
  tokenInSymbol: string;
  tokenOutSymbol: string;
  owner?: string;
  correlationId: string;
  endAt?: string;
}

export interface StopOnchainWatchingRequest {
  chainId: string;
  tokenIn: string;
  tokenOut: string;
  protocol: DexProtocol;
  correlationId: string;
}

export interface UpdateOnchainOwnerRequest {
  chainId: string;
  tokenIn: string;
  tokenOut: string;
  protocol: DexProtocol;
  owner: string | null;
}

// ============================================================================
// Generic Success Response
// ============================================================================

export interface OnchainWatcherSuccessResponse {
  success: boolean;
  message: string;
}
